import enCommon from '../locales/en/common.json';
import viCommon from '../locales/vi/common.json';
import { SEO_PAYLOAD } from '../generated/seoPayload.js';

/** Marketing + list routes always prerendered. Articles are runtime routes. */
export const STATIC_PRERENDER_ROUTES = [
  '/',
  '/practices',
  '/practices/corporate',
  '/practices/licensing',
  '/practices/labour',
  '/practices/privacy',
  '/practices/realestate',
  '/practices/disputes',
  '/team',
  '/toolkits',
  '/updates',
  '/contact',
];

/** Full route list for vite-prerender-plugin.
 * Team profiles remain prerendered. Legal Updates articles are served dynamically.
 */
export const PRERENDER_ROUTES = (() => {
  const teamRoutes = Object.keys(SEO_PAYLOAD?.team ?? {}).map(
    (slug) => `/team/${slug}`,
  );
  return [...new Set([...STATIC_PRERENDER_ROUTES, ...teamRoutes])];
})();

const LOCALES = { en: enCommon, vi: viCommon };

const ROUTE_SEO_KEYS = {
  '/': 'home',
  '/practices': 'practices',
  '/practices/corporate': 'corporate',
  '/practices/licensing': 'licensing',
  '/practices/labour': 'labour',
  '/practices/privacy': 'privacy',
  '/practices/realestate': 'realestate',
  '/practices/disputes': 'disputes',
  '/team': 'team',
  '/contact': 'contact',
  '/toolkits': 'toolkits',
  '/updates': 'updates',
};

function normalizePath(pathname) {
  if (!pathname) return '/';
  const path =
    pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  return path;
}

export function seoKeyForPath(pathname) {
  const path = normalizePath(pathname);
  if (path.startsWith('/team/') && path !== '/team') return 'team';
  if (path.startsWith('/updates/') && path !== '/updates') return 'updates';
  // An unrecognised path is the 404 route; inheriting the homepage's title
  // here is what made a broken link look like a real page.
  return ROUTE_SEO_KEYS[path] ?? 'notFound';
}

/**
 * @returns {{
 *   title: string,
 *   description: string,
 *   lang: string,
 *   ogLocale: string,
 *   ogType?: string,
 *   keywords?: string,
 *   jsonLd?: object | null,
 * }}
 */
export function metaForPath(pathname, lng = 'en') {
  const locale = lng?.startsWith('vi') ? 'vi' : 'en';
  const path = normalizePath(pathname);

  const profileMatch = path.match(/^\/team\/([^/]+)$/);
  if (profileMatch) {
    const slug = profileMatch[1];
    const m = SEO_PAYLOAD?.team?.[slug];
    if (m) {
      return {
        title: m.title,
        description: m.description,
        lang: 'en',
        ogLocale: 'en_US',
        ogType: 'profile',
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: m.name,
          jobTitle: m.role || undefined,
          description: m.description,
          url: `https://dilinh.com/team/${slug}`,
          worksFor: {
            '@type': 'LegalService',
            name: 'Dilinh Legal',
            url: 'https://dilinh.com/',
          },
        },
      };
    }
  }

  const key = seoKeyForPath(path);
  const seo = LOCALES[locale].seo[key] ?? LOCALES[locale].seo.home;
  return {
    title: seo.title,
    description: seo.description,
    lang: locale,
    ogLocale: locale === 'vi' ? 'vi_VN' : 'en_US',
    ogType: 'website',
  };
}
